import { UseFormReturn } from 'react-hook-form'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'
import { useAppDispatch } from '@/globalStore/hooks.ts'
import { UninstallApp } from '@/modules/utilities/uninstallApp/types/UninstallForm.ts'
import useHandleAPIError, { TAPIError } from '@/core/hooks/useHandleAPIError.ts'
import UninstallAppService from '@/modules/utilities/uninstallApp/services/UninstallAppService.ts'
import { uninstallAppActions } from '@/modules/utilities/uninstallApp/store/uninstallAppSlice/UninstallAppSlice.ts'
import { useUninstallBySupplierContext } from '@/modules/utilities/uninstallApp/provider/UninstallAppBySupplierProvider.tsx'

const useUninstallAppForm = (form: UseFormReturn<UninstallApp>) => {
  const dispatch = useAppDispatch()
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const { handleAPIError } = useHandleAPIError()
  const { setValue } = form
  const { articleData } = useUninstallBySupplierContext()

  const onSubmit = useCallback(() => {
    setConfirmDialogOpen(true)
  }, [])

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialogOpen(false)
  }, [])

  const {
    data: uninstallApp,
    mutate: searchDevice,
    isPending
  } = useMutation({
    mutationKey: ['searchDevice'],
    mutationFn: (registersNumber: string) =>
      UninstallAppService.getUninstallAppData(
        registersNumber,
        articleData.supplier!.id
      ),
    onError: (error: TAPIError) => {
      if (error.status === 422) {
        toast.error(
          'Registro no encontrado. Verifique el número e intente nuevamente'
        )
      } else {
        handleAPIError(error, 'Error buscando dispositivo.')
      }
    }
  })

  const { mutate: uninstallDevice, isPending: uninstallingDevice } =
    useMutation({
      mutationKey: ['uninstallDevice'],
      mutationFn: (registersNumber: string) =>
        UninstallAppService.setUninstallApp(
          registersNumber,
          articleData.supplier!.id
        ),
      onError: (error, registerNumber) => {
        handleAPIError(error, 'Error desintalando dispositivo.')
        toast.error(
          `Error al desinstalar: ${registerNumber}. Contacte al soporte`
        )
      },
      onSuccess: (_data, registerNumber) => {
        toast.success(
          `Dispositivo ${registerNumber} liberado. Aplicación desinstalada correctamente`
        )
        setConfirmDialogOpen(false)
        dispatch(uninstallAppActions.closeModal())
      }
    })

  const handlerConfirm = useCallback(
    (registersNumber: string) => {
      if (!articleData.supplier?.id) throw new Error('NO Supplier ID given')
      uninstallDevice(registersNumber)
    },
    [articleData.supplier?.id, uninstallDevice]
  )

  const handlerSearchDevice = useCallback(
    (registersNumber: string) => {
      if (!articleData.supplier?.id) throw new Error('NO Supplier ID given')

      if (!registersNumber) {
        toast.error('Ingrese un número de registro válido')
      } else {
        searchDevice(registersNumber)
      }
    },
    [articleData.supplier, searchDevice]
  )

  useEffect(() => {
    if (uninstallApp) {
      setValue('imei', uninstallApp?.imei)
      setValue('registersNumber', uninstallApp?.registersNumber)
      setValue('brand', uninstallApp?.brand)
      setValue('model', uninstallApp?.model, { shouldValidate: true })
    }
  }, [setValue, uninstallApp])

  return {
    onSubmit,
    handlerSearchDevice,
    confirmDialogOpen,
    closeConfirmDialog,
    handlerConfirm,
    searchingDevice: isPending,
    uninstallingDevice
  }
}

export default useUninstallAppForm
